/**
 * Dynamic theme-color updater.
 * Extracts <meta name="theme-color" content="..."> from the template HTML
 * and updates index.html's top-level meta tags so mobile status bar matches each page view.
 */
const loadedTemplates = new Set();

export function applyThemeColorForSlug(slug, htmlContent = null) {
  if (!slug) return;
  const cleanSlug = slug.replace(/-template$/, '');
  let content = htmlContent;

  if (!content) {
    const templateEl = document.getElementById(`${cleanSlug}-template`) || document.getElementById(cleanSlug);
    if (templateEl) {
      content = templateEl.textContent || templateEl.innerHTML;
    }
  }

  if (content) {
    const match = content.match(/<meta\s+name=["']theme-color["']\s+content=["']([^"']+)["']/i);
    if (match && match[1]) {
      const color = match[1];
      let metaTheme = document.querySelector('meta[name="theme-color"]');
      if (!metaTheme) {
        metaTheme = document.createElement('meta');
        metaTheme.name = 'theme-color';
        document.head.appendChild(metaTheme);
      }
      metaTheme.setAttribute('content', color);

      let metaBg = document.querySelector('meta[name="background-color"]');
      if (!metaBg) {
        metaBg = document.createElement('meta');
        metaBg.name = 'background-color';
        document.head.appendChild(metaBg);
      }
      metaBg.setAttribute('content', color);
    }
  }
}

/**
 * Ensures that template element <script type="text/plain" id="{slug}-template"> exists in DOM.
 * If missing, fetches /templates/{slug}.html and injects it.
 */
export async function ensureTemplateLoaded(slug) {
  if (!slug) return;
  const templateId = slug.endsWith('-template') ? slug : `${slug}-template`;
  const cleanSlug = slug.replace(/-template$/, '');

  if (document.getElementById(templateId)) {
    applyThemeColorForSlug(cleanSlug);
    return;
  }

  if (loadedTemplates.has(cleanSlug)) {
    applyThemeColorForSlug(cleanSlug);
    return;
  }

  try {
    const templateUrl = `/templates/${cleanSlug}.html`;
    const response = await fetch(templateUrl);
    if (response.ok) {
      const htmlContent = await response.text();
      const scriptEl = document.createElement('script');
      scriptEl.type = 'text/plain';
      scriptEl.id = templateId;
      scriptEl.textContent = htmlContent;
      document.body.appendChild(scriptEl);
      loadedTemplates.add(cleanSlug);
      applyThemeColorForSlug(cleanSlug, htmlContent);
    } else {
      console.warn(`[Router] Could not fetch template ${cleanSlug}: status ${response.status}`);
      if (cleanSlug !== '404') {
        await ensureTemplateLoaded('404');
      }
    }
  } catch (err) {
    console.error(`[Router] Failed to load template ${cleanSlug}:`, err);
  }
}

/**
 * Returns a reference to the REAL switchView function from app-controller.js.
 */
function getRealSwitchView() {
  return window.switchView;
}

/**
 * Initializes slug routing and wraps window.switchView.
 */
export function initRouter() {
  // Pre-load the template for the initial URL slug so it's ready when _0x83e2.init() fires
  const initialPath = window.location.pathname.replace(/^\/|\/$/g, '');
  const initialSlug = (initialPath && initialPath !== 'index.html' && initialPath !== 'dist') ? initialPath.split('/')[0] : 'about';

  ensureTemplateLoaded(initialSlug).then(() => {
    applyThemeColorForSlug(initialSlug);
  });

  // Intercept window.switchView to automatically update meta theme-color whenever view switches
  const intervalId = setInterval(() => {
    if (typeof window.switchView === 'function' && !window.switchView.__wrappedForThemeColor) {
      const originalSwitchView = window.switchView;
      const wrapped = function (targetSlug, ...args) {
        if (targetSlug) {
          applyThemeColorForSlug(targetSlug);
        }
        return originalSwitchView.apply(this, [targetSlug, ...args]);
      };
      wrapped.__wrappedForThemeColor = true;
      window.switchView = wrapped;
      clearInterval(intervalId);
    }
  }, 100);

  // Handle browser back / forward navigation
  window.addEventListener('popstate', async (e) => {
    const appLockOverlay = document.getElementById('app-lock-toast-backdrop');
    if (appLockOverlay && (appLockOverlay.dataset.closing === 'true' || appLockOverlay.style.display === 'flex' || appLockOverlay.style.display === 'block')) {
      return;
    }
    if (e.state && (e.state.modalOpen || e.state.appLockSuggestionModalOpen || e.state.privacyModalOpen || e.state.premiumModalOpen)) {
      return;
    }
    if (e.state && e.state.spaView) {
      const targetSlug = e.state.spaView;
      const cleanSlug = String(targetSlug).toLowerCase();
      if (cleanSlug === 'home' || cleanSlug === 'landing') {
        if (typeof window.closeSPA === 'function') {
          window.closeSPA(true, true);
        }
        applyThemeColorForSlug('home');
        return;
      }
      await ensureTemplateLoaded(targetSlug);
      applyThemeColorForSlug(targetSlug);
      const sv = getRealSwitchView();
      if (typeof sv === 'function') {
        sv(targetSlug, false);
      }
      return;
    }
    const rawPath = window.location.pathname.replace(/^\/|\/$/g, '');
    const pathSlug = rawPath.split('/')[0];
    const targetSlug = (pathSlug && pathSlug !== 'index.html' && pathSlug !== 'home' && pathSlug !== 'landing') ? pathSlug : null;
    if (targetSlug) {
      await ensureTemplateLoaded(targetSlug);
      applyThemeColorForSlug(targetSlug);
      const sv = getRealSwitchView();
      if (typeof sv === 'function') {
        sv(targetSlug, false);
      }
    } else {
      if (typeof window.closeSPA === 'function') {
        window.closeSPA(true, true);
      }
      applyThemeColorForSlug('home');
    }
  });
}

